import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ConversationListItem } from '../../../shared/models/assistant.model';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-conversation-list',
  standalone: true,
  imports: [CommonModule],
  template: `
    <aside class="conversation-sidebar" aria-label="Conversation history">
      <div class="sidebar-header">
        <h3>Conversations</h3>
      </div>

      <p class="dock-hint" *ngIf="matterId">
        Select a previous chat or click New Chat.
      </p>

      <button
        type="button"
        class="new-conversation-btn"
        (click)="onNewConversation()"
        [disabled]="loading || !matterId"
        [title]="!matterId ? 'Select a matter to start a conversation' : 'Start new conversation'"
        aria-label="Start new conversation"
      >
        <span class="icon">+</span>
        New Chat
      </button>

      <div class="no-matter-warning" *ngIf="!matterId">
        <p>⚠️ Please select a matter to use the assistant.</p>
      </div>

      <div class="conversation-list" *ngIf="matterId">
        <p class="loading-state" *ngIf="loading">Loading conversations...</p>

        <p class="empty-state" *ngIf="!loading && conversations.length === 0">
          No conversations yet. Start a new chat to begin.
        </p>

        <button
          type="button"
          class="conversation-item"
          *ngFor="let conv of conversations"
          [class.active]="conv._id === currentConversationId"
          [title]="conv.title || 'Untitled Conversation'"
          (click)="onSelectConversation(conv._id)"
        >
          <div class="conversation-title">
            {{ conv.title || 'Untitled Conversation' }}
          </div>
          <div class="conversation-meta">
            <span class="message-count">{{ conv.messageCount || 0 }} messages</span>
            <span class="last-message" *ngIf="conv.lastMessageAt">
              {{ formatDate(conv.lastMessageAt) }}
            </span>
          </div>
        </button>
      </div>
    </aside>
  `,
  styles: [
    `
      .conversation-sidebar {
        display: grid;
        gap: 0.5rem;
        background: transparent;
        border: none;
        box-shadow: none;
        padding: 0.25rem 0;
        max-height: 75vh;
        overflow: hidden;
        grid-template-rows: auto auto 1fr;
      }

      .sidebar-header {
        display: grid;
        grid-template-columns: 1fr;
        align-items: start;
        gap: 0.25rem;
        padding-bottom: 0.4rem;
        border-bottom: 1px solid var(--g8r-border);
      }

      .sidebar-header h3 {
        margin: 0;
        font-size: 0.68rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--g8r-grey-text);
      }

      .new-conversation-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 0.3rem;
        background: var(--g8r-navy-50);
        color: var(--g8r-navy-700);
        border: 1px solid var(--g8r-navy-200);
        border-radius: 0.45rem;
        padding: 0.35rem 0.65rem;
        width: 100%;
        min-height: unset;
        font-size: 0.78rem;
        font-weight: 600;
        cursor: pointer;
        transition: background 0.15s;
        visibility: visible;
        opacity: 1;
      }

      .new-conversation-btn:hover:not(:disabled) {
        background: var(--g8r-navy-100);
        border-color: var(--g8r-navy-300);
      }

      .new-conversation-btn:disabled {
        opacity: 0.45;
        cursor: not-allowed;
      }

      .new-conversation-btn .icon {
        font-size: 1rem;
        line-height: 1;
      }

      .dock-hint {
        margin: -0.1rem 0 0;
        font-size: 0.72rem;
        color: var(--g8r-grey-text);
      }

      .no-matter-warning {
        background: #fff5f2;
        border: 1px solid rgba(192, 57, 43, 0.28);
        border-radius: 0.9rem;
        padding: 0.7rem;
      }

      .no-matter-warning p {
        margin: 0;
        color: var(--g8r-danger);
        font-size: 0.9rem;
        text-align: center;
      }

      .conversation-list {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        overflow-x: hidden;
        overflow-y: auto;
        padding-right: 0.25rem;
      }

      .loading-state,
      .empty-state {
        text-align: center;
        color: var(--g8r-grey-text);
        font-size: 0.9rem;
        padding: 1.5rem 0.5rem;
        margin: 0;
      }

      .conversation-item {
        display: flex;
        flex-direction: column;
        gap: 0.2rem;
        background: transparent;
        border: 1px solid transparent;
        border-radius: 0.45rem;
        padding: 0.4rem 0.5rem;
        text-align: left;
        cursor: pointer;
        transition: background 0.15s, border-color 0.15s;
      }

      .conversation-item:hover {
        background: var(--g8r-navy-50);
        border-color: var(--g8r-navy-100);
      }

      .conversation-item.active {
        background: var(--g8r-navy-50);
        border-color: var(--g8r-gold-400, #e5a80a);
      }

      .conversation-title {
        font-weight: 500;
        color: var(--g8r-navy-900);
        font-size: 0.8rem;
        line-height: 1.3;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .conversation-meta {
        display: flex;
        align-items: center;
        gap: 0.4rem;
        font-size: 0.7rem;
        color: var(--g8r-grey-text);
      }

      .message-count {
        font-weight: 500;
      }

      .last-message {
        font-style: italic;
      }

      @media (max-width: 767px) {
        .conversation-sidebar {
          max-height: 60vh;
        }
      }
    `,
  ],
})
export class ConversationListComponent {
  @Input() conversations: ConversationListItem[] = [];
  @Input() currentConversationId: string | null = null;
  @Input() loading = false;
  @Input() matterId?: string;

  @Output() selectConversation = new EventEmitter<string>();
  @Output() newConversation = new EventEmitter<void>();

  onSelectConversation(conversationId: string): void {
    this.selectConversation.emit(conversationId);
  }

  onNewConversation(): void {
    this.newConversation.emit();
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  }
}
